import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from './client'
import { useAuth } from '../hooks/useAuth'
import type { RequestDetailResponse } from '../types/analytics'

// Types for admin endpoints
export interface AdminUser {
  user_id: string
  github_id: string
  username: string
  email: string
  role: string
  plan_tier: string
  plan_id: number
  created_at: string
  updated_at: string
}

export interface AdminUsersResponse {
  users: AdminUser[]
  total: number
}

export interface UpdateUserRoleRequest {
  role: string
}

export interface UpdateUserPlanRequest {
  plan_id: number
}

// Admin analytics types
export interface AdminAnalyticsSummary {
  total_users: number
  active_users: number
  total_requests: number
  total_cost_usd: number
  approval_rate: number
}

export interface AdminUserAnalytics {
  user_id: string
  username: string
  requests: number
  cost_usd: number
  approval_rate: number
  last_active: string
}

export interface AdminAnalyticsResponse {
  summary: AdminAnalyticsSummary
  users: AdminUserAnalytics[]
}

// Fetch all users (admin only)
export function useAdminUsers() {
  const { isAuthenticated, isAdmin } = useAuth()

  return useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => apiClient.get<AdminUsersResponse>('/v1/admin/users'),
    enabled: isAuthenticated && isAdmin,
    staleTime: 60 * 1000, // 1 minute
  })
}

// Update a user's role (admin only)
export function useUpdateUserRole() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      apiClient.patch<AdminUser>(`/v1/admin/users/${userId}/role`, { role }),
    onSuccess: () => {
      // Invalidate and refetch users list
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
    },
  })
}

// Update a user's plan (admin only)
export function useUpdateUserPlan() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, planId }: { userId: string; planId: number }) =>
      apiClient.patch<AdminUser>(`/v1/admin/users/${userId}/plan`, { plan_id: planId }),
    onSuccess: () => {
      // Invalidate and refetch users list
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
    },
  })
}

// Fetch admin analytics (admin only)
export function useAdminAnalytics(startDate: string, endDate: string) {
  const { isAuthenticated, isAdmin } = useAuth()

  return useQuery({
    queryKey: ['admin', 'analytics', startDate, endDate],
    queryFn: () =>
      apiClient.get<AdminAnalyticsResponse>('/v1/admin/analytics', {
        start_date: startDate,
        end_date: endDate,
      }),
    enabled: isAuthenticated && isAdmin,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// Admin user detail analytics types
export interface DailyUserAnalytics {
  date: string
  total_requests: number
  approved: number
  rejected: number
  inconclusive: number
  errors: number
  total_input_tokens: number
  total_output_tokens: number
  total_cost_usd: number
}

export interface RequestRecordAdmin {
  request_id: string
  timestamp: string
  verdict: 'APPROVED' | 'REJECTED' | 'INCONCLUSIVE' | 'ERROR'
  file_extension: string
  content_lines: number
  content_chars: number
  total_voting_rounds: number
  pass_votes: number
  fail_votes: number
  discarded_votes: number
  total_input_tokens: number
  total_output_tokens: number
  groq_calls: number
  openrouter_calls: number
  cost_usd: number
  total_duration_ms: number
  voting_duration_ms: number
  summarization_ms: number
  plan_tier: string
  client_version?: string
  editor_type?: string
  http_status: number
  has_full_content: boolean
}

export interface AdminUserDetailResponse {
  user_id: string
  username: string
  total_requests: number
  approved: number
  rejected: number
  inconclusive: number
  errors: number
  total_cost_usd: number
  approval_rate: number
  avg_cost_per_request: number
  last_active: string
  analytics: DailyUserAnalytics[]
  requests: RequestRecordAdmin[]
}

// Fetch admin user analytics for specific user (admin only)
export function useAdminUserAnalytics(userId: string, startDate: string, endDate: string) {
  const { isAuthenticated, isAdmin } = useAuth()

  return useQuery({
    queryKey: ['admin', 'user-analytics', userId, startDate, endDate],
    queryFn: () =>
      apiClient.get<AdminUserDetailResponse>(`/v1/admin/analytics/user/${userId}`, {
        start_date: startDate,
        end_date: endDate,
      }),
    enabled: isAuthenticated && isAdmin && !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// Fetch admin request detail for specific request (admin only)
export function useAdminRequestDetail(requestId: string | null) {
  const { isAuthenticated, isAdmin } = useAuth()

  return useQuery({
    queryKey: ['admin', 'request-detail', requestId],
    queryFn: () => apiClient.get<RequestDetailResponse>(`/v1/admin/analytics/requests/${requestId}`),
    enabled: isAuthenticated && isAdmin && requestId !== null,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}
